"use client";

import { useEffect, useState } from "react";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import {
  META_PIXEL_FLUSH_EVENT,
  claimAndFireMetaEvents,
  fetchMetaPixelConfig,
  isPixelAllowedOnHost,
  loadMetaPixel,
} from "@/lib/metaPixel";

/**
 * Meta Pixel base code and the coded conversion events (see lib/metaPixel.ts).
 *
 * Mounted next to <GoogleTagManager/> in the same three layouts - the public
 * site, the student auth pages and the student dashboard - and never on the
 * teacher or admin side.
 *
 * Claims, once the pixel is loaded:
 *  - when a signed-in user is known (page load, or right after an email
 *    signup or the Google callback signs the user in - which is how the
 *    Google CompleteRegistration survives its redirect);
 *  - when an action asks for it via requestMetaPixelFlush() (a booking).
 */
export default function MetaPixel() {
  const { user } = useCurrentUser();
  const userId = (user as { id?: number | string } | null | undefined)?.id ?? null;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void fetchMetaPixelConfig().then((config) => {
      if (cancelled || !isPixelAllowedOnHost(window.location.hostname, config)) {
        return;
      }

      loadMetaPixel(config.pixel_id);
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (ready && userId !== null) {
      void claimAndFireMetaEvents();
    }
  }, [ready, userId]);

  useEffect(() => {
    if (!ready) {
      return;
    }

    const onFlush = () => {
      void claimAndFireMetaEvents();
    };

    window.addEventListener(META_PIXEL_FLUSH_EVENT, onFlush);

    return () => window.removeEventListener(META_PIXEL_FLUSH_EVENT, onFlush);
  }, [ready]);

  return null;
}
